// Normalized API error shape shared across every store.
//
// Backend context (see docs/v4-migration/API-COVERAGE.md for the full audit):
// - Integrations v4 wraps every response in `BaseResult<T>` (`model`,
//   `correlationId`, `message`) and reports real failures using genuine HTTP
//   status codes (400/403/404/409/422/429/500) rather than a 200 response
//   with an embedded "isValid: false" flag.
// - Integrations v2 wrapped responses in `IResultView` (`isValid`, `code`,
//   `model`, `messages: Alert[]`) and - for business-logic failures raised
//   via IResultService.Error(...) - returned them as HTTP 200, which this
//   app's previous v2 integration never inspected. That was a real, silent
//   failure mode: a v2 business error was indistinguishable from success to
//   this frontend. Handling both status-code-driven (v4) and envelope-driven
//   (legacy) failures here means the normalizer is correct today and remains
//   safe if any endpoint is ever served from an older envelope.
import { AxiosError } from "axios";

export type ApiErrorKind =
  | "badRequest"
  | "unauthorized"
  | "forbidden"
  | "notFound"
  | "conflict"
  | "validation"
  | "rateLimited"
  | "server"
  | "network"
  | "cancelled"
  | "unknown";

export interface ApiError {
  kind: ApiErrorKind;
  status: number | null;
  message: string;
  correlationId: string | null;
  /** e.g. "NotFoundException" - present on typed-exception failures. */
  errorCode: string | null;
  /**
   * Per-field validation messages, e.g. { "WorkerSpecifications": ["Input
   * contains disallowed or potentially dangerous content."] } - confirmed
   * live (see docs/v4-migration/DERAYAH-V4-FINAL-HARDENING-CERT-002.md,
   * "Real Write-Path Verification": a real 400 from PreventSqlInjection
   * validation on WorkerSpecifications had this exact shape, with a
   * generic top-level `message` that told the user nothing useful). Empty
   * object when the failure has no field-level detail (e.g. a 404/409/500).
   */
  fieldErrors: Record<string, string[]>;
}

// Confirmed directly against the live API (see
// docs/v4-migration/DERAYAH-VUE-V4-FULL-MODERNIZATION-CERT-001.md,
// "Live Production Verification"): a real 404 from
// POST int/v4/EmploymentRequest/Check with an unknown request code returned
// exactly {"statusCode":404,"errorCode":"NotFoundException",
// "message":"Recruitment request was not found.","correlationId":"...",
// "errors":{}} - this is the shape of the global exception-handling
// middleware's response, not BaseResult<T> (that envelope is only used for
// success responses; success and failure are two genuinely different
// shapes, both handled here). A real 400 from ASP.NET Core's own automatic
// model validation (e.g. [PreventSqlInjection] rejecting input) has the
// same shape but a generic top-level `message` ("The request is invalid or
// missing required data.") with the actually-useful detail only in
// `errors` - confirmed live via a deliberately-triggered validation
// failure while testing EmploymentRequest/AddFromGuest.
interface V4ErrorBody {
  message?: string;
  correlationId?: string;
  errorCode?: string;
  errors?: Record<string, string[]>;
}

interface V2ErrorBody {
  isValid?: boolean;
  message?: string;
  messages?: Array<{ message?: string }>;
}

function kindForStatus(status: number | null): ApiErrorKind {
  switch (status) {
    case 400:
      return "badRequest";
    case 401:
      return "unauthorized";
    case 403:
      return "forbidden";
    case 404:
      return "notFound";
    case 409:
      return "conflict";
    case 422:
      return "validation";
    case 429:
      return "rateLimited";
    default:
      if (status !== null && status >= 500) return "server";
      return "unknown";
  }
}

function extractFieldErrors(data: unknown): Record<string, string[]> {
  if (!data || typeof data !== "object") return {};
  const errors = (data as V4ErrorBody).errors;
  return errors && typeof errors === "object" ? errors : {};
}

function extractMessage(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;

  // Field-level validation detail is more useful than the generic
  // "The request is invalid or missing required data." ASP.NET Core sends
  // as the top-level message for any [Required]/[MaxLength]/
  // [PreventSqlInjection] failure - prefer it when present.
  const fieldErrors = extractFieldErrors(data);
  const fieldMessages = Object.values(fieldErrors).flat();
  if (fieldMessages.length) return fieldMessages.join(" ");

  const v4 = data as V4ErrorBody;
  if (v4.message) return v4.message;

  const v2 = data as V2ErrorBody;
  if (v2.messages?.length) {
    const joined = v2.messages
      .map((item) => item.message)
      .filter((m): m is string => Boolean(m))
      .join(" ");
    if (joined) return joined;
  }

  return null;
}

const DEFAULT_MESSAGE = "حدث خطأ غير متوقع أثناء الاتصال بالخادم. يرجى المحاولة مرة أخرى.";
const NETWORK_MESSAGE = "تعذر الاتصال بالخادم. يرجى التحقق من الاتصال بالإنترنت والمحاولة مرة أخرى.";

export function toApiError(error: unknown): ApiError {
  if (error instanceof AxiosError) {
    if (error.code === "ERR_CANCELED") {
      return {
        kind: "cancelled",
        status: null,
        message: "",
        correlationId: null,
        errorCode: null,
        fieldErrors: {},
      };
    }

    if (!error.response) {
      return {
        kind: "network",
        status: null,
        message: NETWORK_MESSAGE,
        correlationId: null,
        errorCode: null,
        fieldErrors: {},
      };
    }

    const status = error.response.status;
    const data = error.response.data as V4ErrorBody & V2ErrorBody;
    return {
      kind: kindForStatus(status),
      status,
      message: extractMessage(data) ?? DEFAULT_MESSAGE,
      correlationId: data?.correlationId ?? null,
      errorCode: data?.errorCode ?? null,
      fieldErrors: extractFieldErrors(data),
    };
  }

  return {
    kind: "unknown",
    status: null,
    message: DEFAULT_MESSAGE,
    correlationId: null,
    errorCode: null,
    fieldErrors: {},
  };
}
